import React, { useEffect, useState, useCallback } from 'react';
import { Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, IconButton, CircularProgress, Popover, MenuItem, ListItemIcon, ListItemText, Select } from "@mui/material"
import { PetModal } from "./PetModal"
import { useForm, Controller } from "react-hook-form"
import { toast } from "react-toastify"
import { IMaskInput } from "react-imask";
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { OwnerAPI } from '../api/OwnerAPI';
import ConfirmButton from './ConfirmButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Container } from '../components/Container';
import getZipcodeInformations, { getStates } from '../api/CepAPI';
import { FilterOwner } from './FilterOwner';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';

export const OwnerSpace = ({ pets }) => {
    const [selectedOwner, setSelectedOwner] = useState(null);
    const [openModalOwner, setOpenModalOwner] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [rows, setRows] = useState([]);
    const [loadingCep, setLoadingCep] = useState(false)
    const [statesOptions, setStateOptions] = useState([])
    const [loading, setLoading] = useState({
        create: false,
        edit: false,
        delete: false,
        loadData: false
    });

    const defaultValues = {
        id: '',
        name: '',
        email: '',
        phone: '',
        document: '',
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: '',
        zipcode: '',
    }

    const methods = useForm({ defaultValues });

    const loadData = useCallback(async (data) => {
        setLoading(prev => ({ ...prev, loadData: true }));
        const response = await OwnerAPI.getOwners(data);

        if (response.success) {
            setRows(response.data.owners || []);
            const resultState = await getStates()

            if (resultState.length > 0) {
                setStateOptions(resultState.map(s => s.sigla))
            }
        } else {
            toast.error(response.message || "Erro ao carregar dados")
        }
        setLoading(prev => ({ ...prev, loadData: false }));
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleClick = (event, owner) => {
        setAnchorEl(event.currentTarget);
        setSelectedOwner(owner);
    };

    const handleCloseOwner = () => {
        setAnchorEl(null);
        setSelectedOwner(null);
    }

    async function submit(data) {
        setLoading({ ...loading, create: true });
        const owner = {
            name: data.name,
            phone: data.phone,
            document: data.document,
            street: data.street,
            number: data.number,
            complement: data.complement,
            neighborhood: data.neighborhood,
            city: data.city,
            state: data.state,
            zipcode: data.zipcode,
            email: data.email
        }

        const response = await OwnerAPI.createOwner(owner);

        if (response.success) {
            setOpenModalOwner(false);
            methods.reset(defaultValues);
            loadData();
            toast.success(response.message || 'Dono criado com sucesso');
        } else {
            toast.error(response.message || 'Erro ao criar dono');
        }
        setLoading({ ...loading, create: false });
    }

    const handleSubmitEditOwner = async (data) => {
        setLoading({ ...loading, edit: true });
        const body = {
            name: data.name,
            email: data.email,
            phone: data.phone,
            document: data.document,
            street: data.street,
            number: data.number,
            complement: data.complement,
            neighborhood: data.neighborhood,
            city: data.city,
            state: data.state,
            zipcode: data.zipcode
        }
        const response = await OwnerAPI.updateOwner(data.id, body);
        if (response.success) {
            toast.success(response.message || 'Dono atualizado com sucesso');
            setOpenModalOwner(false);
            methods.reset(defaultValues);
            loadData();
        } else {
            toast.error(response.message || 'Erro ao atualizar dono');
        }
        setLoading({ ...loading, edit: false });
    }

    const handleEditOwner = async (owner) => {
        setLoading({ ...loading, edit: true });
        const response = await OwnerAPI.getOwnerById(owner.id);
        if (response.success) {
            methods.reset(response.data.ownerPet);
        }
        setIsEditing(true);
        setOpenModalOwner(true);
        setLoading({ ...loading, edit: false });
    }

    const handleDeleteOwner = async (owner) => {
        setLoading({ ...loading, delete: true });
        const response = await OwnerAPI.deleteOwner(owner.id);
        if (response.success) {
            toast.success(response.message || 'Dono excluído com sucesso');
            loadData();
        }
        setLoading({ ...loading, delete: false });
    }

    const loadCep = async (cep) => {
        setLoadingCep(true)
        let cepFound
        try {
            if (cep) {
                cepFound = await getZipcodeInformations(cep);
            }
        }
        catch (error) {
            console.log(error)
            setLoadingCep(false)
            toast.warning("Ocorreu um erro ao carregar CEP")
            return
        }

        if (cepFound.erro) {
            methods.setError("zipcode", { message: "CEP inválido" });
        } else {
            methods.clearErrors("zipcode");
            methods.setValue("street", cepFound.logradouro || "");
            methods.setValue("neighborhood", cepFound.bairro || "");
            methods.setValue("city", cepFound.localidade || "");
            methods.setValue("state", cepFound.uf || "");
        }

        setLoadingCep(false)
        return cepFound
    }

    let cep = methods.watch("zipcode")

    useEffect(() => {
        const cepClean = (cep || "").replace(/\D/g, '');
        if (cepClean.length === 8) {
            loadCep(cepClean)
        }
    }, [cep])

    return (
        <>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleCloseOwner}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        minWidth: 120,
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                    }
                }}
            >
                <MenuItem
                    onClick={() => {
                        handleEditOwner(selectedOwner);
                        handleCloseOwner();
                    }}
                    sx={{ py: 1 }}
                >
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Editar</ListItemText>
                </MenuItem>
                <MenuItem
                    sx={{ py: 1, color: 'error.main', p: 0 }}
                >
                    <ConfirmButton
                        title="Excluir Dono"
                        message={`Tem certeza que deseja excluir o dono "${selectedOwner?.name}"? Esta ação não pode ser desfeita.`}
                        confirmText="Excluir"
                        cancelText="Cancelar"
                        confirmColor="error"
                        variant="text"
                        size="small"
                        onConfirm={() => handleDeleteOwner(selectedOwner)}
                        onCancel={handleCloseOwner}
                        sx={{
                            width: '100%',
                            justifyContent: 'flex-start',
                            color: 'error.main',
                            textTransform: 'none',
                            p: 1.5
                        }}
                    >
                        <Box display="flex" alignItems="center" className="gap-1">
                            <DeleteIcon />
                            <Typography>
                                Excluir
                            </Typography>
                        </Box>
                    </ConfirmButton>
                </MenuItem>
            </Popover>

            <PetModal
                open={openModalOwner}
                onClose={() => {
                    setOpenModalOwner(false);
                    setIsEditing(false);
                    methods.reset(defaultValues);
                }}
                title={isEditing ? "Editar Dono" : "Novo Dono"}
                size={'small'}
                formId='owner-form'
                loading={isEditing ? loading.edit : loading.create}
            >
                <form id='owner-form' onSubmit={methods.handleSubmit(isEditing ? handleSubmitEditOwner : submit)}>
                    <Box display='flex' flexDirection='column' className='gap-1'>
                        <Controller
                            name="name"
                            control={methods.control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    id="outlined-basic"
                                    label="Nome"
                                    variant="outlined"
                                />
                            )}
                        />
                        <Controller
                            name="email"
                            control={methods.control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    id="outlined-basic"
                                    label="Email"
                                    variant="outlined"
                                    type="email"
                                />
                            )}
                        />
                        <Controller
                            name="phone"
                            control={methods.control}
                            render={({ field: { ref, ...field } }) => (
                                <TextField
                                    {...field}
                                    id="outlined-basic"
                                    label="Telefone"
                                    variant="outlined"
                                    inputRef={ref}
                                    InputProps={{
                                        inputComponent: IMaskInput,
                                    }}
                                    inputProps={{
                                        mask: "(00)00000-0000"
                                    }}
                                    InputLabelProps={{ shrink: !!field.value }}
                                />
                            )}
                        />
                        <Controller
                            name="document"
                            control={methods.control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    id="outlined-basic"
                                    label="Documento"
                                    variant="outlined"
                                    InputProps={{
                                        inputComponent: IMaskInput,
                                    }}
                                    inputProps={{
                                        mask: "000.000.000-00"
                                    }}
                                    InputLabelProps={{ shrink: !!field.value }}
                                />
                            )}
                        />
                        <Controller
                            name="zipcode"
                            control={methods.control}
                            rules={{
                                required: "CEP é obrigatório",
                                pattern: {
                                    value: /^\d{5}-\d{3}$/,
                                    message: "CEP deve estar no formato 00000-000"
                                }
                            }}
                            render={({ field: { ref, ...field }, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    id="outlined-basic"
                                    label="CEP"
                                    variant="outlined"
                                    inputRef={ref}
                                    error={!!error}
                                    helperText={error?.message}
                                    InputProps={{
                                        inputComponent: IMaskInput,
                                    }}
                                    inputProps={{
                                        mask: "00000-000"
                                    }}
                                    InputLabelProps={{ shrink: !!field.value }}
                                />
                            )}
                        />
                        <Controller
                            name="state"
                            control={methods.control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    select
                                    id="outlined-basic"
                                    label="Estado"
                                    variant="outlined"
                                    disabled={loadingCep}
                                >
                                    {statesOptions.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}
                        />
                        <Controller
                            name="city"
                            control={methods.control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    id="outlined-basic"
                                    label="Cidade"
                                    variant="outlined"
                                    disabled={loadingCep}
                                />
                            )}
                        />
                        <Controller
                            name="neighborhood"
                            control={methods.control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    id="outlined-basic"
                                    label="Bairro"
                                    variant="outlined"
                                    disabled={loadingCep}
                                />
                            )}
                        />
                        <Controller
                            name="street"
                            control={methods.control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    id="outlined-basic"
                                    label="Endereço"
                                    variant="outlined"
                                    disabled={loadingCep}
                                />
                            )}
                        />

                        <Controller
                            name="number"
                            control={methods.control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    id="outlined-basic"
                                    label="Número"
                                    variant="outlined"
                                />
                            )}
                        />
                        <Controller
                            name="complement"
                            control={methods.control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    id="outlined-basic"
                                    label="Complemento"
                                    variant="outlined"
                                />
                            )}
                        />
                    </Box>
                </form>
            </PetModal>

            <Box marginBottom="10px" width="100%" display="flex" justifyContent="flex-end" className="gap-1" margin={{ xs: 2 }}>
                <Button
                    style={{ textTransform: "none" }}
                    color="primary"
                    variant="contained"
                    onClick={() => {
                        setIsEditing(false);
                        methods.reset(defaultValues);
                        setOpenModalOwner(true);
                    }}
                >
                    <Typography>
                        Novo Dono
                    </Typography>
                </Button>
                <FilterOwner onFilter={loadData} pets={pets} />
            </Box>


            <Box display={{ xs: 'block', sm: 'none' }}>
                {loading.loadData ? (
                    <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
                        <CircularProgress size={20} />
                    </Box>
                ) : rows.length === 0 ? (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="200px" sx={{ color: '#9CA3AF' }}>
                        <Typography variant="body1" color="textSecondary">
                            Nenhum dono encontrado
                        </Typography>
                    </Box>
                ) : (
                    rows.map((row) => (
                        <Card key={row.id} sx={{ mb: 2, borderRadius: 2, boxShadow: 3 }}>
                            <CardHeader
                                title={
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {row.name}
                                    </Typography>
                                }
                                action={
                                    <IconButton
                                        onClick={(event) => handleClick(event, row)}
                                        size="small"
                                    >
                                        <MoreHorizIcon />
                                    </IconButton>
                                }
                            />
                             <CardContent>
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    <Typography variant="body2" color="textSecondary">
                                        Telefone:
                                    </Typography>
                                    <Typography variant="body2">
                                        {row.phone}
                                    </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    <Typography variant="body2" color="textSecondary">
                                        Documento:
                                    </Typography>
                                    <Typography variant="body2">
                                        {row.document}
                                    </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="body2" color="textSecondary">
                                        Email:
                                    </Typography>
                                    <Typography variant="body2">
                                        {row.email}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    ))
                )}
            </Box>


            <Box display={{ xs: 'none', sm: 'block' }} >
                <Container>

                    <Box display={{ xs: 'none', sm: 'block' }}>
                        {loading.loadData ? (
                            <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
                                <CircularProgress size={20} />
                            </Box>
                        ) : (
                            <TableContainer component={Paper}>
                                <Table sx={{ width: '100%', height: '100%', overflowY: 'auto' }} aria-label="simple table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Nome</TableCell>
                                            <TableCell>Telefone</TableCell>
                                            <TableCell>Documento</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell align="right">Ações</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.length > 0 ? (
                                            rows.map((row) => (
                                                <TableRow
                                                    key={row.id}
                                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                                >
                                                    <TableCell component="th" scope="row">
                                                        {row.name}
                                                    </TableCell>
                                                    <TableCell>{row.phone}</TableCell>
                                                    <TableCell>{row.document}</TableCell>
                                                    <TableCell>{row.email}</TableCell>
                                                    <TableCell align="right">
                                                        <IconButton
                                                            onClick={(event) => handleClick(event, row)}
                                                            size="small"
                                                        >
                                                            <MoreHorizIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">
                                                    Nenhum dono encontrado
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Container>
            </Box>
        </>
    )
}